import 'dayjs/locale/sv';

import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { XCircle } from 'react-feather';

import { Button, Link, Modal, Tags, Typography, Wrapper } from '@/components';
import { useAuthToken } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { useCollaborations } from '@/hooks/client';
import { useOverlayBackHandler } from '@/hooks/useOverlayBackHandler';
import { PlatformIcon } from '@/utils/platformIcon';
import { TranslatedLink } from '@/utils/translatedComponents';
import { getDiscoveryProfileUrl } from '@/utils/discovery';

import styles from './CampaignPageCollaborations.module.css';

interface CampaignPageCollaborationsProps {
  campaignId: string;
}

export function CampaignPageCollaborations({ campaignId }: CampaignPageCollaborationsProps) {
  const [showAddress, setShowAddress] = useState('');
  const [influencer, setInfluencer] = useState<InfluencerType | null>(null);
  const [showLinks, setShowLinks] = useState<Record<string, TypeAny[]>>();
  const { t } = useTranslation();
  const token = useAuthToken();

  const handleCloseInfluencer = useCallback(() => setInfluencer(null), []);
  const closeInfluencer = useOverlayBackHandler(!!influencer, handleCloseInfluencer);

  const { data = {}, isLoading } = useCollaborations(campaignId);

  const memoData = useMemo(
    () =>
      ((data.collaborations || []) as Api.Client.Collaboration[]).filter((row) =>
        ['ONGOING', 'DONE'].includes(row.status)
      ),
    [data]
  );

  function handleLinks(linkData: Api.Client.Collaboration) {
    let linkGroups = {
      permalink: [] as string[],
      target_url: [] as string[],
      tracker: [] as Record<string, TypeAny>[]
    };

    linkData.products?.forEach((link) => {
      linkGroups.permalink.push(link.permalink);
      linkGroups.target_url.push(link.target_url);
    });

    linkGroups = {
      permalink: [...new Set(linkGroups.permalink)].filter((link) => link),
      target_url: [...new Set(linkGroups.target_url)].filter((link) => link),
      tracker: linkData.trackers
    };

    setShowLinks(linkGroups);
  }

  function sanitizeUrl(input: string): string {
    try {
      const url = new URL(input);
      return url.origin + url.pathname;
    } catch {
      return input;
    }
  }

  return (
    <>
      {showLinks && (
        <Modal handleClose={() => setShowLinks(undefined)}>
          <div className={styles.campaignPageCollaborationsModalProduct}>
            <div className={styles.campaignPageCollaborationsModalProductContainer}>
              <Typography sizes={['label']}>
                <Typography sizes={['bold']}>
                  {`${t('campaignPageCollaborations.permalink')}: `}
                </Typography>

                {showLinks.permalink.length
                  ? showLinks.permalink.map((link) => (
                      <>
                        <br />
                        <Link key={`permalink-${link}`} target="_blank" href={link}>
                          {link}
                        </Link>
                      </>
                    ))
                  : '-'}
              </Typography>

              <span>
                <Typography sizes={['label']}>
                  <Typography sizes={['bold']}>
                    {`${t('campaignPageCollaborations.targetUrl')}: `}
                  </Typography>

                  {showLinks.target_url.length
                    ? showLinks.target_url.map((link) => (
                        <>
                          <br />
                          <Link key={`target-url-${link}`} target="_blank" href={link}>
                            {link}
                          </Link>
                        </>
                      ))
                    : '-'}
                </Typography>
              </span>

              <span>
                <Typography sizes={['label']}>
                  <Typography sizes={['bold']}>
                    {`${t('campaignPageCollaborations.trackerUrl')}: `}
                  </Typography>

                  {showLinks.tracker.length
                    ? showLinks.tracker.map((link) => (
                        <>
                          <br />
                          <Link
                            key={`tracker-url-${link.tracker_url}`}
                            target="_blank"
                            href={link.tracker_url}
                          >
                            {link.tracker_url}
                          </Link>
                          {link.tracker_note && ` (${link.tracker_note})`}
                        </>
                      ))
                    : '-'}
                </Typography>
              </span>
            </div>
          </div>
        </Modal>
      )}

      {showAddress && (
        <Modal handleClose={() => setShowAddress('')}>
          <div
            className={styles.campaignPageCollaborationsModalAddress}
            dangerouslySetInnerHTML={{ __html: showAddress.replace(/\n/g, '<br />') }}
          />
        </Modal>
      )}

      {influencer && (
        <div className={styles.campaignPageCollaborationsIframe} onClick={() => closeInfluencer()}>
          <button
            className={styles.campaignPageCollaborationsIframeClose}
            onClick={(e) => {
              e.stopPropagation();
              closeInfluencer();
            }}
            type="button"
          >
            <XCircle size={24} />
          </button>
          <div
            className={styles.campaignPageCollaborationsIframeSrc}
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              title={`${influencer.handle} profile`}
              className={styles.campaignPageCollaborationsIframeFrame}
              src={getDiscoveryProfileUrl(influencer.influencer_id, token)}
              allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
            />
          </div>
        </div>
      )}

      <Wrapper
        heading={t('campaignPageCollaborations.heading')}
        isLoading={isLoading}
        campaignId={campaignId}
      >
        <div className={styles.campaignPageCollaborationsList}>
          {memoData.length ? (
            <>
              <div className={styles.campaignPageCollaborationsListRow}>
                <div>
                  <Typography sizes={['label-secondary']}>
                    {t('campaignPageCollaborations.influencer')}
                  </Typography>
                </div>
                <div>
                  <Typography sizes={['label-secondary']}>
                    {t('campaignPageCollaborations.beats')}
                  </Typography>
                </div>
                <div>
                  <Typography sizes={['label-secondary']}>
                    {t('campaignPageCollaborations.followers')}
                  </Typography>
                </div>
                <div style={{ paddingLeft: '5px' }}>
                  <Typography sizes={['label-secondary']}>
                    {t('campaignPageCollaborations.engagement')}
                  </Typography>
                </div>
                <div>
                  <Typography sizes={['label-secondary']}>
                    {t('campaignPageCollaborations.deadline')}
                  </Typography>
                </div>
                <div>
                  <Typography sizes={['label-secondary']}>
                    {t('campaignPageCollaborations.publication')}
                  </Typography>
                </div>
                <div>
                  <Typography sizes={['label-secondary']}>
                    {t('campaignPageCollaborations.code')}
                  </Typography>
                </div>
                <div>
                  <Typography sizes={['label-secondary']}>
                    {t('campaignPageCollaborations.address')}
                  </Typography>
                </div>
                <div>
                  <Typography sizes={['label-secondary']}>
                    {t('campaignPageCollaborations.links')}
                  </Typography>
                </div>
                <div data-right-align="true">
                  <Typography sizes={['label-secondary']}>
                    {t('campaignPageCollaborations.status')}
                  </Typography>
                </div>
              </div>

              {memoData.map((collaboration) => {
                const hasProductLinks =
                  collaboration.products?.some(
                    (product) => product.permalink || product.target_url
                  ) || !!collaboration.trackers.length;
                const deadlineDates = [
                  ...new Set(collaboration.products?.map((product) => product.date_draft) || [])
                ].filter((date) => date);
                const publishDates = [
                  ...new Set(collaboration.products?.map((product) => product.date_publish) || [])
                ].filter((date) => date);

                return (
                  <div
                    className={styles.campaignPageCollaborationsListRow}
                    key={`collaborations-${collaboration.collaboration_id}`}
                  >
                    <button
                      className={styles.campaignPageCollaborationsListRowIframe}
                      onClick={() => setInfluencer(collaboration.influencer)}
                      type="button"
                    >
                      <div className={styles.campaignPageCollaborationsListRowImage}>
                        <div className={styles.campaignPageCollaborationsListRowImageContainer}>
                          <img
                            className={styles.campaignPageCollaborationsListRowImageContainerSrc}
                            src={collaboration.influencer?.mugshot_url || ''}
                            alt={collaboration.influencer?.name || ''}
                            // eslint-disable-next-line no-return-assign
                            onError={(e: TypeAny) => (e.target.style.opacity = 0)}
                          />
                        </div>
                        <div>
                          {collaboration.influencer?.name || '-'}

                          {collaboration.influencer?.handle && (
                            <div className={styles.campaignPageCollaborationsListRowPlatform}>
                              <div className={styles.campaignPageCollaborationsListRowPlatformIcon}>
                                {PlatformIcon(collaboration.platform)}
                              </div>

                              <span className={styles.link}>
                                <Typography sizes={['label']}>
                                  {sanitizeUrl(collaboration.influencer.handle)}
                                </Typography>
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                    <div data-is-beats>
                      <span className={styles.campaignPageCollaborationsListRowMobile}>
                        <Typography sizes={['bold']}>
                          {`${t('campaignPageCollaborations.beats')}: `}
                        </Typography>
                      </span>
                      {collaboration.beats.map((beat) => (
                        <div key={`collaboration-beat-${beat.platform}-${beat.beat_name}`}>
                          <TranslatedLink
                            href={`/connect/campaigns/${campaignId}/beat/${beat.beat_id}`}
                          >
                            <span className={styles.campaignPageCollaborationsListRowPlatform}>
                              <div className={styles.campaignPageCollaborationsListRowPlatformIcon}>
                                {PlatformIcon(beat.platform)}
                              </div>

                              <Typography sizes={['label']}>{beat.beat_name}</Typography>
                            </span>
                          </TranslatedLink>
                        </div>
                      ))}
                    </div>
                    <div>
                      <span className={styles.campaignPageCollaborationsListRowMobile}>
                        <Typography sizes={['bold']}>
                          {`${t('campaignPageCollaborations.followers')}: `}
                        </Typography>
                      </span>
                      {collaboration.influencer?.followers_count
                        ? collaboration.influencer.followers_count
                        : '-'}
                    </div>
                    <div>
                      <span className={styles.campaignPageCollaborationsListRowMobile}>
                        <Typography sizes={['bold']}>
                          {`${t('campaignPageCollaborations.engagement')}: `}
                        </Typography>
                      </span>
                      {collaboration.influencer?.engagement_rate
                        ? `${(collaboration.influencer.engagement_rate * 100).toFixed(2)}%`
                        : '-'}
                    </div>
                    <div>
                      <span className={styles.campaignPageCollaborationsListRowMobile}>
                        <Typography sizes={['bold']}>
                          {`${t('campaignPageCollaborations.deadline')}: `}
                        </Typography>
                      </span>
                      {deadlineDates.length
                        ? deadlineDates.map((date) => (
                            <div key={`deadline-date-deadline-${date}`}>
                              {dayjs(date).format('YYYY-MM-DD')}
                            </div>
                          ))
                        : '-'}
                    </div>
                    <div>
                      <span className={styles.campaignPageCollaborationsListRowMobile}>
                        <Typography sizes={['bold']}>
                          {`${t('campaignPageCollaborations.publication')}: `}
                        </Typography>
                      </span>
                      {publishDates.length
                        ? publishDates.map((date) => (
                            <div key={`deadline-date-deadline-${date}`}>
                              {dayjs(date).format('YYYY-MM-DD')}
                            </div>
                          ))
                        : '-'}
                    </div>
                    <div>
                      <span className={styles.campaignPageCollaborationsListRowMobile}>
                        <Typography sizes={['bold']}>
                          {`${t('campaignPageCollaborations.code')}: `}
                        </Typography>
                      </span>
                      -
                    </div>
                    <div>
                      {collaboration.delivery_address ? (
                        <Button onClick={() => setShowAddress(collaboration.delivery_address)}>
                          {t('campaignPageCollaborations.show')}
                          <span className={styles.campaignPageCollaborationsListRowMobile}>
                            &nbsp;
                            {t('campaignPageCollaborations.address')}
                          </span>
                        </Button>
                      ) : (
                        <span className={styles.campaignPageCollaborationsListRowDesktop}>-</span>
                      )}
                    </div>
                    <div>
                      {hasProductLinks ? (
                        <Button onClick={() => handleLinks(collaboration)}>
                          {t('campaignPageCollaborations.show')}
                          <span
                            className={styles.campaignPageCollaborationsListRowMobile}
                            data-is-button
                          >
                            {` ${t('campaignPageCollaborations.links')}`}
                          </span>
                        </Button>
                      ) : (
                        '-'
                      )}
                    </div>
                    <div data-right-align="true">
                      <Tags
                        tags={[
                          {
                            value: t(`translatedType.${collaboration.status}`),
                            theme: collaboration.status
                          }
                        ]}
                      />
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <Typography sizes={['h4']}>{t('campaignPageCollaborations.notFound')}</Typography>
          )}
        </div>
      </Wrapper>
    </>
  );
}
